import React, { useState } from "react";
import { IoClose } from "react-icons/io5";
import { ContactMessage } from "../../types/contactMessages";
import { useAppDispatch } from "../../redux/hooks";
import { replyToContactMessage } from "../../redux/thunk";
import { Preloader } from "../elements/Preloader";

interface ContactMessageModalProps {
  message: ContactMessage;
  closeModal: () => void;
}

const ContactMessageModal = ({ message, closeModal }: ContactMessageModalProps) => {
  const [reply, setReply] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    setIsSubmitting(true);
    try {
      await dispatch(
        replyToContactMessage({ contactId: message._id, message: reply })
      ).unwrap();
      closeModal();
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-3xl rounded-3xl bg-white shadow-2xl flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-8 pb-4">
          <div className="space-y-1">
            <h3 className="text-2xl font-bold text-black">{message.email}</h3>
            <p className="text-xl font-bold text-[#008060]">{message.phoneNumber || "No phone number"}</p>
          </div>
          <button
            onClick={closeModal}
            className="rounded-full border border-gray-300 p-1 hover:bg-gray-100 transition-colors"
          >
            <IoClose className="h-8 w-8 text-black" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-10 space-y-8">
          {/* User Message */}
          <div className="rounded-2xl bg-[#F0F0F0] p-8 shadow-sm">
            <p className="text-[#333] text-base leading-relaxed">
              {message.message}
            </p>
          </div>

          {/* Previous Responses */}
          {message.responses && message.responses.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-bold text-gray-800 text-lg">Previous Responses</h4>
              {message.responses.map((res) => (
                <div key={res._id} className="rounded-xl border border-gray-100 p-5 bg-white shadow-sm border-l-4 border-l-secondary">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-secondary bg-green-50 px-2 py-1 rounded">
                      Sent by: {res.sentBy}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(res.dateSent).toLocaleString('en-US', { 
                        dateStyle: 'medium', 
                        timeStyle: 'short' 
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed font-medium">
                    {res.message}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Reply Area */}
          <div className="space-y-4 pt-4 border-t border-gray-50">
            <label htmlFor="adminResponse" className="block text-lg font-bold text-black">
              Admin Response
            </label>
            <div className="relative">
              <textarea
                id="adminResponse"
                rows={4}
                placeholder="Type your message here...."
                className="w-full rounded-2xl border border-gray-200 p-6 text-base focus:border-[#008060] focus:outline-none focus:ring-1 focus:ring-[#008060] transition-all bg-white"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-6 p-10 pt-0">
          <button
            onClick={closeModal}
            className="rounded-2xl bg-[#F5F5F5] px-16 py-4 text-lg font-semibold text-[#333] hover:bg-gray-200 transition-all active:scale-95"
          >
            Back
          </button>
          <button
            onClick={handleSendReply}
            disabled={!reply.trim() || isSubmitting}
            className={`flex items-center justify-center rounded-2xl px-16 py-4 text-lg font-semibold text-white shadow-lg transition-all active:scale-95 ${
              !reply.trim() || isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#008060] hover:bg-[#006b50] shadow-[0_4px_14px_0_rgba(0,128,96,0.39)]"
            }`}
          >
            {isSubmitting ? <Preloader /> : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactMessageModal;
